import {
	type Area,
	type Booking,
	type SessionInstance,
	apiAppBookingSessioninstanceSearch
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { bookingStore, updateBookingStore } from './booking';
import { businessStore } from './business';
import { calendarDateRange } from './calendarDates';
import { t } from './i18n';
import { toastError } from './toast';

export const sessionInstanceStore = writable<SessionInstance[]>([]);

const inFlightRequests = new Map<string, Promise<void>>();

export function clearSessionInstanceStore() {
	inFlightRequests.clear();
	sessionInstanceStore.set([]);
}

export function loadSessionInstance(
	date: DateTime | undefined = undefined,
	offset = 0
): Promise<void> {
	if (!get(businessStore).id) return Promise.resolve();

	const startDate = (date ?? get(calendarDateRange).start).toISODate()!;
	const endDate = (date ?? get(calendarDateRange).end).toISODate()!;
	const requestKey = `${startDate}_${endDate}_${offset}`;

	const inFlight = inFlightRequests.get(requestKey);
	if (inFlight) {
		return inFlight;
	}

	const offsetLoading = 250;
	const promise = apiAppBookingSessioninstanceSearch({
		startDate,
		endDate,
		offset,
		limit: offsetLoading
	})
		.then((res) => {
			const items = res.items;
			sessionInstanceStore.set([
				...(date
					? get(sessionInstanceStore).filter(
							(sessionInstance) => !sessionInstance.date.hasSame(date, 'day')
						)
					: get(sessionInstanceStore)
				).filter(
					(sessionInstance) =>
						!items.find(
							(si) => si.sessionInstanceId === sessionInstance.sessionInstanceId
						)
				),
				...items
			]);

			if (items.length === offsetLoading) {
				return loadSessionInstance(date, offset + offsetLoading);
			}
		})
		.finally(() => {
			inFlightRequests.delete(requestKey);
		});

	inFlightRequests.set(requestKey, promise);
	return promise;
}

calendarDateRange.subscribe(() => {
	if (!get(businessStore).id) return;
	loadSessionInstance();
});

const planningItemsClickObservers = new Set<
	(sessionInstance: SessionInstance, date: DateTime, area: Area) => void
>();
export function planningItemClick(sessionInstance: SessionInstance, date: DateTime, area: Area) {
	planningItemsClickObservers.forEach((observer) => observer(sessionInstance, date, area));
}
export function planningItemClickAddObserver(
	observer: (sessionInstance: SessionInstance, date: DateTime, area: Area) => void
): () => void {
	planningItemsClickObservers.add(observer);
	return () => planningItemsClickObservers.delete(observer);
}

export async function mergeSessionInstances(
	sourceSessionInstance: SessionInstance,
	targetSessionInstance: SessionInstance,
	date: DateTime,
	saveBooking: (booking: Booking) => Promise<void>
): Promise<boolean> {
	if (targetSessionInstance.sessionId !== sourceSessionInstance.sessionId) return false;

	const sourceSessionInstanceId = sourceSessionInstance.sessionInstanceId;
	const targetSessionInstanceId = targetSessionInstance.sessionInstanceId;

	// Snapshot of current state for rollback
	const previousBookings = get(bookingStore);
	const previousSessionInstances = get(sessionInstanceStore);

	const movingBookings = previousBookings.filter((b) =>
		b.sessions.some((s) => s.sessionInstanceId === sourceSessionInstanceId)
	);

	const updatedBookings = movingBookings.map((b) => ({
		...b,
		date: targetSessionInstance.date,
		sessions: b.sessions.map((s) =>
			s.sessionInstanceId === sourceSessionInstanceId
				? { ...s, sessionInstanceId: targetSessionInstanceId }
				: s
		)
	}));

	// Optimistic local update
	updatedBookings.forEach((b) => updateBookingStore(b));
	sessionInstanceStore.set(
		previousSessionInstances.filter((s) => s.sessionInstanceId !== sourceSessionInstanceId)
	);

	try {
		await Promise.all(updatedBookings.map((b) => saveBooking(b)));
		await loadSessionInstance(date);
		return true;
	} catch (err) {
		console.error('Failed to merge session instances', err);
		// Rollback optimistic updates
		bookingStore.set(previousBookings);
		sessionInstanceStore.set(previousSessionInstances);
		toastError(get(t)('planning.error.merge-failed'));
		await loadSessionInstance(date);
		return false;
	}
}
